import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InfoRounded as InfoRoundedIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@src/redux/actions';
import ReferralPopup from '@src/components/ReferralPopup';
import apis from '@src/apis';
import { isValidHttpUrl, removeQueryParams } from '@src/utils/url';
import {
  AUTO_APPLY_REFERRAL_PARAM,
  REFERRAL_CODE_PARAM,
} from '@src/constants/referral';
import { extractReferralCodeFromUrl } from '@src/services/referral';
import {
  StyledReferralDialogErrorMessage,
  StyledReferralDialogInput,
  StyledReferralDialogNote,
} from './index.style';
import RedeemDialogSuccess from './RedeemDialogSuccess';

const RedeemDialog = ({
  appliedReferralErrorMessage,
  referralCode,
  rewardCredits,
  openRewardDialog,
  setOpenRewardDialog,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const { isOpeningRedeemReferralDialog } = useSelector((state) => state.user);
  const [
    isOpeningRedeemReferralDialogSuccess,
    setIsOpeningRedeemReferralDialogSuccess,
  ] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const dispatch = useDispatch();

  const [code, setCode] = useState(referralCode || '');
  const [credits, setCredits] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChangeCode = (e) => {
    setCode(e.target.value);
    setErrorMessage('');
  };

  const handlePaste = (e) => {
    // Prevent default paste behavior to avoid pasting the referral link from the clipboard
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    if (isValidHttpUrl(text)) {
      const codeFromUrl = extractReferralCodeFromUrl(text);
      setCode(codeFromUrl);
    } else {
      setCode(text);
    }
  };

  const handleClose = () => {
    setCode('');
    setErrorMessage('');
    dispatch(actions.user.updateIsOpeningRedeemReferralDialog(false));
    if (referralCode) {
      history.replace({
        pathname: location.pathname,
        search: removeQueryParams(location.search, [
          REFERRAL_CODE_PARAM,
          AUTO_APPLY_REFERRAL_PARAM,
        ]),
      });
    }
  };

  const handleConfirm = async () => {
    setIsRedeeming(true);
    // Check user eligibility before apply referral
    const resultCheckEligibility = await apis.referral.checkUserEligibility();
    if (!resultCheckEligibility.status) {
      setCode('');
      setIsRedeeming(false);
      dispatch(actions.user.openReferralErrorNotiPopup());
      dispatch(actions.user.updateIsOpeningRedeemReferralDialog(false));
      return;
    }

    // Apply referral
    const response = await apis.referral.applyReferral(code);
    if (response.status) {
      dispatch(actions.user.updateIsOpeningRedeemReferralDialog(false));
      setIsOpeningRedeemReferralDialogSuccess(true);
      const refereeReward = response?.result?.refereeRewards?.find(
        (reward) => reward.type === 'character',
      );
      setCredits(refereeReward?.details?.characters || 0);
      dispatch(actions.auth.updateReferrerId(response?.result?.referrerId));
    } else {
      setErrorMessage(t('invalidReferralCode'));
    }
    setIsRedeeming(false);
  };

  const handleCloseRedeemReferralDialogSuccess = async () => {
    setIsOpeningRedeemReferralDialogSuccess(false);
    setOpenRewardDialog(false);
    // This is for update user info after redeem referral code
    // to make sure user can see the correct total characters
    const userRes = await apis.account.getMe();
    dispatch(actions.auth.updateUserInfo(userRes.result));
  };

  const renderContent = () => (
    <>
      <StyledReferralDialogInput
        variant="filled"
        fullWidth
        value={code}
        onChange={handleChangeCode}
        placeholder={t('referralRedeemDialogTitle')}
        onPaste={handlePaste}
      />
      {errorMessage && (
        <StyledReferralDialogErrorMessage>
          {errorMessage}
        </StyledReferralDialogErrorMessage>
      )}
      <StyledReferralDialogNote>
        <InfoRoundedIcon className="note-icon" />
        <Typography className="note-text">
          {t('referralRedeemDialogDescription')}
        </Typography>
      </StyledReferralDialogNote>
    </>
  );

  const renderAction = () => (
    <Button
      variant="contained"
      className="dialog-actions-button"
      disabled={!code.trim() || isRedeeming}
      onClick={handleConfirm}
    >
      {isRedeeming ? (
        <CircularProgress
          value={75}
          className="circular-progress"
          size="24px"
        />
      ) : (
        t('confirm')
      )}
    </Button>
  );

  useEffect(() => {
    if (appliedReferralErrorMessage) {
      setErrorMessage(appliedReferralErrorMessage);
    }
  }, [appliedReferralErrorMessage]);

  return (
    <>
      <ReferralPopup
        title={t('referralRedeemDialogTitle')}
        contentComponent={renderContent()}
        actionComponent={renderAction()}
        open={isOpeningRedeemReferralDialog}
        onClose={handleClose}
      />
      <RedeemDialogSuccess
        open={isOpeningRedeemReferralDialogSuccess || openRewardDialog}
        onClose={handleCloseRedeemReferralDialogSuccess}
        credits={credits || rewardCredits}
        referralCode={referralCode}
      />
    </>
  );
};

export default RedeemDialog;
